import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  CircularProgress,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import api from '@src/api/clients';

const STATUS_OPTIONS = [
  { value: 'new_business', label: 'New Business' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'active_new', label: 'Active - New' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'off', label: 'Off' },
];

const PHONE_FORMAT_OPTIONS = ['PLAIN', 'INTERNATIONAL', '(NANP)', 'US_FORMAT'];

const BATH_MANUFACTURER_OPTIONS = [
  { value: 'bci', label: 'BCI' },
  { value: 'jacuzzi', label: 'Jacuzzi' },
  { value: 'kohler', label: 'Kohler' },
  { value: 'samuel_mueller', label: 'Samuel Mueller (BCI)' },
];

const STATUS_LABEL_BY_VALUE = STATUS_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const BATH_LABEL_BY_VALUE = BATH_MANUFACTURER_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const RELATED_SECTIONS = [
  {
    title: 'Operations',
    items: [
      { label: 'Client ZIPs', to: (client) => `/clients/client-zips?client_id=${client.id}` },
      { label: 'Client Lanes', to: () => '/clients/lanes' },
      { label: 'Leads', to: (client) => `/leads?client_id=${client.id}` },
      { label: 'Client Inbox', to: () => '/client-inbox' },
      { label: 'Onboarding', to: () => '/clients/onboarding' },
      { label: 'Invite Members', to: () => '/clients/invite-members' },
    ],
  },
  {
    title: 'Marketing',
    items: [
      { label: 'Client Offers', to: (client) => `/client-offers?client_id=${client.id}` },
      { label: 'Landing Pages', to: (client) => `/clients/client-landing-pages?client_id=${client.id}` },
      { label: 'Daily Pulse', to: () => '/daily-pulse' },
      { label: 'Performance Trends', to: () => '/performance-trends' },
      { label: 'Ad Name Builder', to: () => '/ad-builder' },
      { label: 'Campaign Builder', to: () => '/facebook/campaign-builder' },
    ],
  },
  {
    title: 'Data & Integrations',
    items: [
      { label: 'Mappings', to: (client) => `/email-mappings?client_id=${client.id}` },
      { label: 'Backend Data', to: () => '/backend-data' },
      { label: 'Data Backfill', to: () => '/data-backfill' },
      { label: 'Task Logger', to: () => '/app1/tasklogger' },
      { label: 'Knowledge Base', to: () => '/knowledge-base' },
      { label: 'Tasks', to: () => '/tasks' },
    ],
  },
  {
    title: 'Revenue & Planning',
    items: [
      { label: 'Client Budgets', to: (client) => `/client-budgets?client_id=${client.id}` },
      { label: 'Invoices', to: () => '/dashboard/invoices' },
      { label: 'Client Dashboard', to: (client) => `/clients/${client.slug || client.id}/dashboard` },
      { label: 'Client Performance', to: () => '/client-performance' },
      { label: 'Creative Performance', to: () => '/creative-performance' },
    ],
  },
];

const DETAIL_SECTIONS = [
  {
    title: 'Business Profile',
    fields: [
      { key: 'name', label: 'Client Name', type: 'text', editable: true },
      { key: 'slug', label: 'Slug', type: 'text', editable: false },
      { key: 'status', label: 'Status', type: 'select', editable: true, options: STATUS_OPTIONS },
      { key: 'parent_company', label: 'Parent Company', type: 'parent_company_select', editable: true },
      { key: 'tier', label: 'Tier', type: 'text', editable: false },
      { key: 'active', label: 'Active', type: 'boolean', editable: true },
      {
        key: 'bath_manufacturer',
        label: 'Bath Manufacturer',
        type: 'select',
        editable: true,
        options: BATH_MANUFACTURER_OPTIONS,
      },
    ],
  },
  {
    title: 'Launch & Goals',
    fields: [
      { key: 'launch_date', label: 'Launch Date', type: 'date', editable: true },
      { key: 'desired_launch_date', label: 'Desired Launch Date', type: 'date', editable: true },
      { key: 'cost_of_marketing_goal', label: 'Marketing Goal %', type: 'number', editable: true },
      { key: 'fees', label: 'Fees %', type: 'number', editable: true },
    ],
  },
  {
    title: 'Lead Routing & Webhooks',
    fields: [
      {
        key: 'phone_format',
        label: 'Phone Format',
        type: 'select',
        editable: true,
        options: PHONE_FORMAT_OPTIONS,
      },
      { key: 'skip_webhook', label: 'Skip Webhook', type: 'boolean', editable: true },
      { key: 'skip_lead_update_task', label: 'Skip Lead Update Task', type: 'boolean', editable: true },
      { key: 'skip_out_of_area_leads', label: 'Skip Out of Area Leads', type: 'boolean', editable: true },
    ],
  },
  {
    title: 'Email & Notifications',
    fields: [
      { key: 'regular_lead_email', label: 'Regular Lead Email', type: 'boolean', editable: true },
      { key: 'self_book_email', label: 'Self-Book Email', type: 'boolean', editable: true },
      { key: 'renter_email', label: 'Renter Email', type: 'boolean', editable: true },
      { key: 'email_recipients', label: 'Email Recipients', type: 'text', editable: true },
    ],
  },
  {
    title: 'Google Sheet Configuration',
    fields: [
      { key: 'google_spreadsheet_url', label: 'Google Spreadsheet URL', type: 'url', editable: true },
      {
        key: 'spreadsheet_fields',
        label: 'Spreadsheet Fields',
        type: 'multiline',
        editable: true,
      },
    ],
  },
  {
    title: 'Testing & Notes',
    fields: [
      { key: 'test_zip', label: 'Test ZIP', type: 'text', editable: true },
      { key: 'test_address', label: 'Test Address', type: 'text', editable: true },
      { key: 'last_backend_update', label: 'Last Backend Update', type: 'datetime', editable: false },
    ],
  },
];

const WEBHOOK_CONFIG_GROUPS = [
  {
    id: 'primary',
    title: 'Primary Webhook Configuration',
    fields: [
      { key: 'webhook', label: 'Webhook URL', type: 'url', editable: true },
      { key: 'send_as_form_encoded', label: 'Send As Form Encoded', type: 'boolean', editable: true },
      { key: 'success_post_keyword', label: 'Success Keyword', type: 'text', editable: true },
      { key: 'custom_headers', label: 'Headers (JSON)', type: 'json', editable: true },
      { key: 'payload_mapping', label: 'Payload Mapping (JSON)', type: 'json', editable: true },
    ],
  },
  {
    id: 'self_book',
    title: 'Self-Book Webhook Configuration',
    fields: [
      { key: 'webhook_selfbook', label: 'Webhook URL', type: 'url', editable: true },
      { key: 'self_book_send_as_form_encoded', label: 'Send As Form Encoded', type: 'boolean', editable: true },
      { key: 'self_book_success_keyword', label: 'Success Keyword', type: 'text', editable: true },
      { key: 'self_book_custom_headers', label: 'Headers (JSON)', type: 'json', editable: true },
      { key: 'self_book_payload_mapping', label: 'Payload Mapping (JSON)', type: 'json', editable: true },
    ],
  },
  {
    id: 'secondary',
    title: 'Secondary Webhook Configuration',
    fields: [
      { key: 'secondary_webhook', label: 'Webhook URL', type: 'url', editable: true },
      { key: 'secondary_send_as_form_encoded', label: 'Send As Form Encoded', type: 'boolean', editable: true },
      { key: 'secondary_custom_headers', label: 'Headers (JSON)', type: 'json', editable: true },
      { key: 'secondary_payload_mapping', label: 'Payload Mapping (JSON)', type: 'json', editable: true },
    ],
  },
  {
    id: 'secondary_self_book',
    title: 'Secondary Self-Book Webhook Configuration',
    fields: [
      { key: 'secondary_webhook_selfbook', label: 'Webhook URL', type: 'url', editable: true },
      { key: 'secondary_self_book_send_as_form_encoded', label: 'Send As Form Encoded', type: 'boolean', editable: true },
      { key: 'secondary_self_book_custom_headers', label: 'Headers (JSON)', type: 'json', editable: true },
      { key: 'secondary_self_book_payload_mapping', label: 'Payload Mapping (JSON)', type: 'json', editable: true },
    ],
  },
];

const JSON_FIELD_KEYS = WEBHOOK_CONFIG_GROUPS.flatMap((group) =>
  group.fields.filter((field) => field.type === 'json').map((field) => field.key)
);

const DEFAULT_WEBHOOK_GROUPS = {
  primary: true,
  self_book: false,
  secondary: false,
  secondary_self_book: false,
};

function isUrl(value = '') {
  return /^https?:\/\//i.test(String(value || '').trim());
}

function formatFieldValue(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
}

function parseDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function displayDate(value) {
  const parsed = parseDate(value);
  return parsed ? parsed.toLocaleString() : '—';
}

function toDateInputValue(value) {
  const parsed = parseDate(value);
  if (!parsed) return '';
  return parsed.toISOString().slice(0, 10);
}

function toPayloadValue(field, value) {
  if (value === undefined) {
    return undefined;
  }
  if (field.key === 'email_recipients') {
    if (value === '' || value === null) {
      return '';
    }
    return String(value)
      .split(/[\n,]/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .join(',');
  }
  if (field.type === 'parent_company_select') {
    return value === '' || value === null ? null : Number(value);
  }
  if (field.key === 'fees') {
    return value === '' || value === null ? null : Number(value) / 100;
  }
  if (field.type === 'number') {
    return value === '' || value === null ? null : Number(value);
  }
  if (field.type === 'date') {
    return value || null;
  }
  return value;
}

function toFeesPercentValue(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '';
  }
  return (numeric * 100).toFixed(2);
}

function buildDraftFromClient(clientData) {
  const next = { ...(clientData || {}) };
  next.fees = toFeesPercentValue(clientData?.fees);
  next.email_recipients = String(clientData?.email_recipients || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .join('\n');
  return next;
}

function hasConfigValue(value, fieldType) {
  if (value === null || value === undefined || value === '') return false;
  if (fieldType === 'boolean') return Boolean(value);
  if (fieldType === 'json') {
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
      } catch (_err) {
        return value.trim().length > 0 && value.trim() !== '{}';
      }
    }
    return value && typeof value === 'object' && Object.keys(value).length > 0;
  }
  return String(value).trim().length > 0;
}

function hasWebhookGroupData(record, group) {
  return group.fields.some((field) => hasConfigValue(record?.[field.key], field.type));
}

function formatJsonForEditor(value) {
  if (value === null || value === undefined || value === '') {
    return '{}';
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return JSON.stringify(parsed, null, 2);
    } catch (_err) {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch (_err) {
    return '{}';
  }
}

function normalizeActivityItem(source, row, client) {
  const id = row?.id || `${source}-${Math.random()}`;

  if (source === 'offers') {
    return {
      id: `offer-${id}`,
      source: 'Offer',
      title: row?.custom_label || row?.offer_type_display || row?.offer_type || 'Offer updated',
      subtitle: row?.description || row?.vertical_name || client?.name,
      at: row?.updated_at || row?.created_at,
      link: `/client-offers?client_id=${client.id}`,
    };
  }
  if (source === 'landingPages') {
    return {
      id: `lp-${id}`,
      source: 'Landing Page',
      title: row?.name || row?.url || 'Landing page updated',
      subtitle: row?.vertical_name || client?.name,
      at: row?.updated_at || row?.created_at,
      link: `/clients/client-landing-pages?client_id=${client.id}`,
    };
  }
  if (source === 'budgets') {
    return {
      id: `budget-${id}`,
      source: 'Budget',
      title: row?.status || 'Budget updated',
      subtitle: row?.amount ? `$${Number(row.amount).toLocaleString()}` : row?.date || client?.name,
      at: row?.updated_at || row?.created_at || row?.date,
      link: `/client-budgets?client_id=${client.id}`,
    };
  }
  if (source === 'zips') {
    return {
      id: `zip-${id}`,
      source: 'ZIP',
      title: row?.zip || row?.zip_code || 'ZIP updated',
      subtitle: row?.vertical_name || row?.territory_name || client?.name,
      at: row?.date_updated || row?.updated_at || row?.date_created,
      link: `/clients/client-zips?client_id=${client.id}`,
    };
  }
  if (source === 'emailMappings') {
    return {
      id: `email-map-${id}`,
      source: 'Mapping',
      title: row?.name || 'Email mapping updated',
      subtitle: 'Email Mapping',
      at: row?.updated_at || row?.created_at,
      link: `/email-mappings?client_id=${client.id}&category=email`,
    };
  }
  if (source === 'sftpMappings') {
    return {
      id: `sftp-map-${id}`,
      source: 'Mapping',
      title: row?.name || 'SFTP mapping updated',
      subtitle: 'SFTP Mapping',
      at: row?.updated_at || row?.created_at,
      link: `/email-mappings?client_id=${client.id}&category=sftp`,
    };
  }
  if (source === 'apiMappings') {
    return {
      id: `api-map-${id}`,
      source: 'Mapping',
      title: row?.name || 'API mapping updated',
      subtitle: 'API Mapping',
      at: row?.updated_at || row?.created_at,
      link: `/email-mappings?client_id=${client.id}&category=api`,
    };
  }
  if (source === 'googleSheetMappings') {
    return {
      id: `gs-map-${id}`,
      source: 'Mapping',
      title: row?.name || 'Google Sheet mapping updated',
      subtitle: 'Google Sheet Mapping',
      at: row?.updated_at || row?.created_at,
      link: `/email-mappings?client_id=${client.id}&category=google_sheet`,
    };
  }

  return null;
}

function HubField({
  field,
  client,
  draft,
  isEditing,
  onChange,
  jsonDraft,
  jsonErrors,
  onJsonChange,
  parentCompanyOptions,
  parentCompanyLookup,
}) {
  const value = isEditing ? draft[field.key] : client?.[field.key];

  if (isEditing && field.editable) {
    if (field.type === 'boolean') {
      return (
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
            {field.label}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              checked={Boolean(value)}
              onChange={(e) => onChange(field.key, e.target.checked)}
              size="small"
            />
            <Typography variant="body2">{value ? 'Yes' : 'No'}</Typography>
          </Stack>
        </Grid>
      );
    }

    if (field.type === 'select') {
      const normalizedOptions = (field.options || []).map((option) =>
        typeof option === 'string' ? { value: option, label: option } : option
      );
      return (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              label={field.label}
              value={value || ''}
              onChange={(e) => onChange(field.key, e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {normalizedOptions.map((option) => (
                <MenuItem key={`${field.key}-${option.value}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === 'parent_company_select') {
      return (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>{field.label}</InputLabel>
            <Select
              label={field.label}
              value={value ?? ''}
              onChange={(e) => {
                const nextValue = e.target.value;
                onChange(field.key, nextValue === '' ? '' : Number(nextValue));
              }}
            >
              <MenuItem value="">None</MenuItem>
              {parentCompanyOptions.map((option) => (
                <MenuItem key={`pc-${option.value}`} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      );
    }

    if (field.type === 'date') {
      return (
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label={field.label}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={toDateInputValue(value)}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        </Grid>
      );
    }

    if (field.type === 'json') {
      const rawValue = jsonDraft[field.key] ?? '{}';
      const errorText = jsonErrors[field.key] || '';
      return (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {field.label}
          </Typography>
          <Box
            sx={{
              border: '1px solid',
              borderColor: errorText ? 'error.main' : 'divider',
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: '#0f172a',
            }}
          >
            <Box
              component="textarea"
              value={rawValue}
              onChange={(e) => onJsonChange(field.key, e.target.value)}
              spellCheck={false}
              rows={12}
              sx={{
                width: '100%',
                border: 0,
                outline: 0,
                resize: 'vertical',
                p: 1.25,
                m: 0,
                fontFamily: "'Roboto Mono', monospace",
                fontSize: '0.8rem',
                lineHeight: 1.5,
                color: '#e5e7eb',
                backgroundColor: '#0f172a',
              }}
            />
          </Box>
          <Typography variant="caption" color={errorText ? 'error.main' : 'text.secondary'} sx={{ mt: 0.5, display: 'block' }}>
            {errorText || 'Valid JSON required. Example: {\"key\": \"value\"}'}
          </Typography>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          size="small"
          label={field.label}
          type={field.type === 'number' ? 'number' : field.type === 'url' ? 'url' : 'text'}
          multiline={field.type === 'multiline' || field.key === 'email_recipients'}
          minRows={field.type === 'multiline' || field.key === 'email_recipients' ? 4 : undefined}
          value={value ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      </Grid>
    );
  }

  let finalValue = formatFieldValue(value);
  if (field.type === 'parent_company_select') {
    const lookupKey = String(value || '');
    finalValue = parentCompanyLookup[lookupKey] || client?.parent_company_name || 'None';
  }
  if (field.key === 'fees' && !isEditing) {
    finalValue = value === null || value === undefined || value === ''
      ? '—'
      : `${toFeesPercentValue(value)}%`;
  }
  if (field.key === 'status' && value) {
    finalValue = STATUS_LABEL_BY_VALUE[value] || finalValue;
  }
  if (field.key === 'email_recipients' && value) {
    finalValue = String(value)
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .join('\n');
  }
  if (field.key === 'bath_manufacturer' && value) {
    finalValue = BATH_LABEL_BY_VALUE[value] || finalValue;
  }
  if ((field.type === 'date' || field.type === 'datetime') && parseDate(value)) {
    finalValue = displayDate(value);
  }
  if (field.type === 'json') {
    finalValue = formatJsonForEditor(value);
  }

  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
        {field.label}
      </Typography>
      {field.type !== 'json' && isUrl(finalValue) ? (
        <Typography
          component="a"
          href={finalValue}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
        >
          {finalValue}
        </Typography>
      ) : (
        <Typography
          variant="body2"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: field.type === 'json' ? 'monospace' : 'inherit',
          }}
        >
          {finalValue}
        </Typography>
      )}
    </Grid>
  );
}

export default function ClientHubPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [draft, setDraft] = useState({});
  const [jsonDraft, setJsonDraft] = useState({});
  const [jsonErrors, setJsonErrors] = useState({});
  const [enabledWebhookGroups, setEnabledWebhookGroups] = useState(DEFAULT_WEBHOOK_GROUPS);
  const [expandedWebhookGroups, setExpandedWebhookGroups] = useState(DEFAULT_WEBHOOK_GROUPS);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsMessage, setDetailsMessage] = useState('');
  const [activity, setActivity] = useState([]);
  const [parentCompanyOptions, setParentCompanyOptions] = useState([]);
  const [parentCompanyLookup, setParentCompanyLookup] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const logoInputRef = useRef(null);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testConfigType, setTestConfigType] = useState('primary');
  const [testLeadJson, setTestLeadJson] = useState(
    JSON.stringify(
      {
        id: 'test-lead-123',
        first_name: 'Test',
        last_name: 'Lead',
        phone: '5551234567',
        email: 'test@example.com',
        address: '123 Test St',
        city: 'San Diego',
        state: 'CA',
        zip_code: '92121',
      },
      null,
      2
    )
  );
  const [testJsonError, setTestJsonError] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');
  const [testCopyMessage, setTestCopyMessage] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      setClient(null);
      setActivity([]);
      setIsEditingDetails(false);
      setDetailsMessage('');
      setJsonDraft({});
      setJsonErrors({});
      setLogoFile(null);
      setLogoPreviewUrl('');
      setEnabledWebhookGroups(DEFAULT_WEBHOOK_GROUPS);
      setExpandedWebhookGroups(DEFAULT_WEBHOOK_GROUPS);
      try {
        const fields = [
          'id',
          'name',
          'slug',
          'parent_company',
          'client_logo_file',
          'client_logo_url',
          'status',
          'tier',
          'active',
          'parent_company_name',
          'vertical_tags',
          'bath_manufacturer',
          'launch_date',
          'desired_launch_date',
          'goal_number',
          'cost_of_marketing_goal',
          'fees',
          'send_as_form_encoded',
          'success_post_keyword',
          'custom_headers',
          'payload_mapping',
          'webhook',
          'webhook_selfbook',
          'self_book_payload_mapping',
          'self_book_custom_headers',
          'self_book_send_as_form_encoded',
          'self_book_success_keyword',
          'secondary_webhook',
          'secondary_payload_mapping',
          'secondary_custom_headers',
          'secondary_send_as_form_encoded',
          'secondary_webhook_selfbook',
          'secondary_self_book_payload_mapping',
          'secondary_self_book_custom_headers',
          'secondary_self_book_send_as_form_encoded',
          'phone_format',
          'skip_webhook',
          'skip_lead_update_task',
          'skip_out_of_area_leads',
          'regular_lead_email',
          'self_book_email',
          'renter_email',
          'email_recipients',
          'google_spreadsheet_url',
          'spreadsheet_fields',
          'test_zip',
          'test_address',
          'last_backend_update',
        ].join(',');

        const clientRes = await api.request({
          url: `dashboard/clients/${clientId}/?fields=${fields}`,
          method: 'GET',
        });
        const clientData = clientRes?.data;
        setClient(clientData);
        setDraft(buildDraftFromClient(clientData));
        const initialJsonDraft = {};
        JSON_FIELD_KEYS.forEach((key) => {
          initialJsonDraft[key] = formatJsonForEditor(clientData?.[key]);
        });
        setJsonDraft(initialJsonDraft);
        setIsEditingDetails(searchParams.get('edit') === '1');
        const nextEnabled = { ...DEFAULT_WEBHOOK_GROUPS };
        WEBHOOK_CONFIG_GROUPS.forEach((group) => {
          if (hasWebhookGroupData(clientData, group)) {
            nextEnabled[group.id] = true;
          }
        });
        setEnabledWebhookGroups(nextEnabled);
        setExpandedWebhookGroups(nextEnabled);

        if (clientData?.slug && clientData.slug !== clientId) {
          navigate(`/clients/${clientData.slug}`, { replace: true });
        }

        const clientPk = clientData?.id;
        if (!clientPk) {
          return;
        }

        try {
          const parentsRes = await api.request({
            url: 'dashboard/clients/',
            method: 'GET',
            payload: {
              fields: 'parent_company,parent_company_name',
              limit: 500,
            },
          });
          const rows = Array.isArray(parentsRes?.data)
            ? parentsRes.data
            : Array.isArray(parentsRes?.data?.results)
              ? parentsRes.data.results
              : [];
          const unique = new Map();
          rows.forEach((row) => {
            if (row?.parent_company && row?.parent_company_name) {
              unique.set(String(row.parent_company), row.parent_company_name);
            }
          });
          const options = Array.from(unique.entries())
            .map(([value, label]) => ({ value: Number(value), label }))
            .sort((a, b) => a.label.localeCompare(b.label));
          const lookup = {};
          options.forEach((option) => {
            lookup[String(option.value)] = option.label;
          });
          setParentCompanyOptions(options);
          setParentCompanyLookup(lookup);
        } catch (_err) {
          setParentCompanyOptions([]);
          setParentCompanyLookup({});
        }

        const [
          offersRes,
          landingPagesRes,
          budgetsRes,
          zipsRes,
          emailMappingsRes,
          sftpMappingsRes,
          apiMappingsRes,
          googleSheetMappingsRes,
        ] = await Promise.allSettled([
          api.request({ url: 'dashboard/client-offers/', method: 'GET', payload: { client_id: clientPk } }),
          api.request({ url: 'dashboard/client-landing-pages/', method: 'GET', payload: { client_id: clientPk } }),
          api.request({ url: 'dashboard/client-budgets/', method: 'GET', payload: { client_id: clientPk, page: 1, page_size: 10 } }),
          api.request({ url: 'dashboard/client-zips/', method: 'GET', payload: { client_id: clientPk, page: 1, page_size: 10 } }),
          api.request({ url: `app1/email-mappings/?client=${clientPk}&limit=10`, method: 'GET' }),
          api.request({ url: `app1/sftp-mappings/?client=${clientPk}&limit=10`, method: 'GET' }),
          api.request({ url: `app1/api-mappings/?client=${clientPk}&limit=10`, method: 'GET' }),
          api.request({ url: `app1/google-sheet-mappings/?client=${clientPk}&limit=10`, method: 'GET' }),
        ]);

        const bundles = [
          ['offers', offersRes],
          ['landingPages', landingPagesRes],
          ['budgets', budgetsRes],
          ['zips', zipsRes],
          ['emailMappings', emailMappingsRes],
          ['sftpMappings', sftpMappingsRes],
          ['apiMappings', apiMappingsRes],
          ['googleSheetMappings', googleSheetMappingsRes],
        ];

        const items = bundles.flatMap(([source, result]) => {
          if (result.status !== 'fulfilled') {
            return [];
          }
          const data = result.value?.data;
          const rows = Array.isArray(data)
            ? data
            : Array.isArray(data?.results)
              ? data.results
              : [];
          return rows
            .map((row) => normalizeActivityItem(source, row, clientData))
            .filter(Boolean);
        });

        items.sort((a, b) => {
          const aTs = parseDate(a.at)?.getTime() || 0;
          const bTs = parseDate(b.at)?.getTime() || 0;
          return bTs - aTs;
        });

        setActivity(items.slice(0, 15));
      } catch (err) {
        setError(err?.response?.data?.detail || 'Failed to load client hub');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [clientId, navigate]);

  useEffect(() => () => {
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
  }, [logoPreviewUrl]);

  const detailCards = useMemo(() => DETAIL_SECTIONS, []);
  const availableWebhookTestOptions = useMemo(() => {
    if (!client) return [{ value: 'primary', label: 'Primary Webhook' }];
    const options = [];
    if (client.webhook) options.push({ value: 'primary', label: 'Primary Webhook' });
    if (client.webhook_selfbook) options.push({ value: 'self_book', label: 'Self-Book Webhook' });
    if (client.secondary_webhook) options.push({ value: 'secondary', label: 'Secondary Webhook' });
    if (client.secondary_webhook_selfbook) {
      options.push({ value: 'secondary_self_book', label: 'Secondary Self-Book Webhook' });
    }
    return options.length ? options : [{ value: 'primary', label: 'Primary Webhook' }];
  }, [client]);

  const handleDraftChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDetailsMessage('');
  };

  const handleJsonDraftChange = (field, value) => {
    setJsonDraft((prev) => ({ ...prev, [field]: value }));
    setJsonErrors((prev) => ({ ...prev, [field]: '' }));
    setDetailsMessage('');
  };

  const handleStartEdit = () => {
    setDraft(buildDraftFromClient(client));
    setLogoFile(null);
    setLogoPreviewUrl('');
    const nextJsonDraft = {};
    JSON_FIELD_KEYS.forEach((key) => {
      nextJsonDraft[key] = formatJsonForEditor(client?.[key]);
    });
    setJsonDraft(nextJsonDraft);
    setJsonErrors({});
    const nextEnabled = { ...DEFAULT_WEBHOOK_GROUPS };
    WEBHOOK_CONFIG_GROUPS.forEach((group) => {
      if (hasWebhookGroupData(client, group)) {
        nextEnabled[group.id] = true;
      }
    });
    setEnabledWebhookGroups(nextEnabled);
    setExpandedWebhookGroups(nextEnabled);
    setIsEditingDetails(true);
    setDetailsMessage('');
    const next = new URLSearchParams(searchParams);
    next.set('edit', '1');
    setSearchParams(next, { replace: true });
  };

  const handleCancelEdit = () => {
    setDraft(buildDraftFromClient(client));
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    setLogoPreviewUrl('');
    setLogoFile(null);
    const nextJsonDraft = {};
    JSON_FIELD_KEYS.forEach((key) => {
      nextJsonDraft[key] = formatJsonForEditor(client?.[key]);
    });
    setJsonDraft(nextJsonDraft);
    setJsonErrors({});
    const nextEnabled = { ...DEFAULT_WEBHOOK_GROUPS };
    WEBHOOK_CONFIG_GROUPS.forEach((group) => {
      if (hasWebhookGroupData(client, group)) {
        nextEnabled[group.id] = true;
      }
    });
    setEnabledWebhookGroups(nextEnabled);
    setExpandedWebhookGroups(nextEnabled);
    setIsEditingDetails(false);
    setDetailsMessage('');
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  const handleSaveDetails = async () => {
    if (!client?.id) {
      return;
    }

    const editableFields = [
      ...DETAIL_SECTIONS.flatMap((section) => section.fields),
      ...WEBHOOK_CONFIG_GROUPS.flatMap((group) => group.fields),
    ].filter((field) => field.editable);

    const payload = {};
    const nextJsonErrors = {};
    editableFields.forEach((field) => {
      if (field.type === 'json') {
        const raw = (jsonDraft[field.key] || '').trim();
        if (!raw) {
          payload[field.key] = {};
          return;
        }
        try {
          payload[field.key] = JSON.parse(raw);
        } catch (_err) {
          nextJsonErrors[field.key] = `Invalid JSON in "${field.label}"`;
        }
        return;
      }
      payload[field.key] = toPayloadValue(field, draft[field.key]);
    });

    if (Object.keys(nextJsonErrors).length > 0) {
      setJsonErrors(nextJsonErrors);
      setDetailsMessage('Please fix invalid JSON fields before saving.');
      return;
    }

    setSavingDetails(true);
    setDetailsMessage('');
    try {
      if (logoFile) {
        payload.client_logo_file = logoFile;
      }
      const response = await api.request({
        url: `dashboard/clients/${client.slug || client.id}/`,
        method: 'PATCH',
        payload,
        multipart: Boolean(logoFile),
      });
      setClient((prev) => ({ ...prev, ...response.data }));
      setDraft(buildDraftFromClient({ ...client, ...response.data }));
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl);
      }
      setLogoPreviewUrl('');
      setLogoFile(null);
      const refreshedJsonDraft = {};
      JSON_FIELD_KEYS.forEach((key) => {
        refreshedJsonDraft[key] = formatJsonForEditor(response.data?.[key]);
      });
      setJsonDraft(refreshedJsonDraft);
      setJsonErrors({});
      const nextEnabled = { ...DEFAULT_WEBHOOK_GROUPS };
      WEBHOOK_CONFIG_GROUPS.forEach((group) => {
        if (hasWebhookGroupData(response.data, group)) {
          nextEnabled[group.id] = true;
        }
      });
      setEnabledWebhookGroups(nextEnabled);
      setExpandedWebhookGroups(nextEnabled);
      setIsEditingDetails(false);
      setDetailsMessage('Client details saved.');
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });

      if (response?.data?.slug && response.data.slug !== clientId) {
        navigate(`/clients/${response.data.slug}`, { replace: true });
      }
    } catch (err) {
      setDetailsMessage(err?.response?.data?.detail || 'Failed to save client details.');
    } finally {
      setSavingDetails(false);
    }
  };

  const handleSelectLogo = () => {
    logoInputRef.current?.click();
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    const nextPreview = URL.createObjectURL(file);
    setLogoFile(file);
    setLogoPreviewUrl(nextPreview);
    setDetailsMessage('');
  };

  const openWebhookTestDialog = () => {
    const first = availableWebhookTestOptions[0]?.value || 'primary';
    setTestConfigType(first);
    setTestDialogOpen(true);
    setTestJsonError('');
    setTestError('');
    setTestResult(null);
    setTestCopyMessage('');
  };

  const handleSendWebhookTest = async () => {
    setTestJsonError('');
    setTestError('');
    setTestResult(null);
    setTestCopyMessage('');
    let parsedLeadData = {};
    try {
      parsedLeadData = JSON.parse(testLeadJson || '{}');
      if (!parsedLeadData || typeof parsedLeadData !== 'object' || Array.isArray(parsedLeadData)) {
        setTestJsonError('Lead JSON must be an object.');
        return;
      }
    } catch (_err) {
      setTestJsonError('Invalid JSON.');
      return;
    }

    setTestSending(true);
    try {
      let overrides = null;
      if (isEditingDetails) {
        const configFieldMap = {
          primary: {
            webhook_url: 'webhook',
            send_as_form_encoded: 'send_as_form_encoded',
            success_keyword: 'success_post_keyword',
            custom_headers: 'custom_headers',
            payload_template: 'payload_mapping',
          },
          self_book: {
            webhook_url: 'webhook_selfbook',
            send_as_form_encoded: 'self_book_send_as_form_encoded',
            success_keyword: 'self_book_success_keyword',
            custom_headers: 'self_book_custom_headers',
            payload_template: 'self_book_payload_mapping',
          },
          secondary: {
            webhook_url: 'secondary_webhook',
            send_as_form_encoded: 'secondary_send_as_form_encoded',
            success_keyword: null,
            custom_headers: 'secondary_custom_headers',
            payload_template: 'secondary_payload_mapping',
          },
          secondary_self_book: {
            webhook_url: 'secondary_webhook_selfbook',
            send_as_form_encoded: 'secondary_self_book_send_as_form_encoded',
            success_keyword: null,
            custom_headers: 'secondary_self_book_custom_headers',
            payload_template: 'secondary_self_book_payload_mapping',
          },
        };

        const mapping = configFieldMap[testConfigType];
        if (mapping) {
          overrides = {
            webhook_url: draft[mapping.webhook_url] || '',
            send_as_form_encoded: Boolean(draft[mapping.send_as_form_encoded]),
          };
          if (mapping.success_keyword) {
            overrides.success_keyword = draft[mapping.success_keyword] || '';
          }

          const parseJsonField = (fieldKey, label) => {
            const raw = jsonDraft[fieldKey];
            if (!raw || !raw.trim()) return {};
            const parsed = JSON.parse(raw);
            if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
              throw new Error(`${label} must be a JSON object.`);
            }
            return parsed;
          };

          try {
            overrides.custom_headers = parseJsonField(mapping.custom_headers, 'Headers');
            overrides.payload_template = parseJsonField(mapping.payload_template, 'Payload mapping');
          } catch (err) {
            setTestError(err?.message || 'Invalid JSON in webhook configuration fields.');
            setTestSending(false);
            return;
          }
        }
      }

      const res = await api.request({
        url: `dashboard/clients/${client?.slug || client?.id}/test-webhook/`,
        method: 'POST',
        payload: {
          config_type: testConfigType,
          lead_data: parsedLeadData,
          overrides,
        },
      });
      setTestResult(res.data);
    } catch (err) {
      const data = err?.response?.data;
      setTestError(
        data?.detail || data?.error || err?.message || 'Failed to send test webhook.'
      );
      if (data) {
        setTestResult(data);
      }
    } finally {
      setTestSending(false);
    }
  };

  const buildWebhookTestTemplate = () => {
    if (!testResult) {
      return '';
    }
    const requestUrl = testResult?.request?.url || 'N/A';
    const requestBodyRaw = testResult?.request?.serialized_data ?? testResult?.request?.payload ?? {};
    let requestBody = requestBodyRaw;
    if (typeof requestBodyRaw === 'string') {
      try {
        requestBody = JSON.parse(requestBodyRaw);
      } catch (_err) {
        requestBody = requestBodyRaw;
      }
    }
    const responseCode = testResult?.response?.status_code ?? testResult?.status_code ?? 'N/A';
    const responseBody = testResult?.response?.body ?? testResult?.body ?? testResult;
    const maybeError = testResult?.error || testError;
    const bodyLines =
      requestBody && typeof requestBody === 'object' && !Array.isArray(requestBody)
        ? Object.entries(requestBody).map(([key, value]) => {
            if (value === null || value === undefined) {
              return `${key}: `;
            }
            if (typeof value === 'object') {
              return `${key}: ${JSON.stringify(value)}`;
            }
            return `${key}: ${String(value)}`;
          })
        : [String(requestBody)];
    const sections = [
      'We sent a test lead to the endpoint. Can you please confirm it on your end?',
      '',
      `Client: ${client?.name || 'Unknown Client'}`,
      `URL: ${requestUrl}`,
      '',
      'POST Body:',
      ...bodyLines,
      '',
      `Response Code: ${responseCode}`,
      'Response Body:',
      JSON.stringify(responseBody, null, 2),
    ];
    if (maybeError) {
      sections.push('', `Error: ${maybeError}`);
    }
    sections.push('', 'Thanks,', 'Jack');
    return sections.join('\n');
  };

  const handleCopyWebhookTemplate = async () => {
    const template = buildWebhookTestTemplate();
    if (!template) {
      setTestCopyMessage('Run a webhook test first.');
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(template);
      } else {
        throw new Error('Clipboard unavailable');
      }
      setTestCopyMessage('Template copied. Paste it into your client email thread.');
    } catch (_err) {
      setTestCopyMessage('Copy failed. You can copy from the result JSON below.');
    }
  };

  const handleEnableWebhookGroup = (groupId) => {
    setEnabledWebhookGroups((prev) => ({ ...prev, [groupId]: true }));
    setExpandedWebhookGroups((prev) => ({ ...prev, [groupId]: true }));
  };

  const handleToggleWebhookGroup = (groupId, isExpanded) => {
    setExpandedWebhookGroups((prev) => ({ ...prev, [groupId]: isExpanded }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: '#c9ddff',
          background: 'linear-gradient(180deg, #f5f9ff 0%, #ffffff 100%)',
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Stack direction="row" spacing={1.2} alignItems="center" mb={0.8}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {logoPreviewUrl || client?.client_logo_file || client?.client_logo_url ? (
                    <img
                      src={logoPreviewUrl || client?.client_logo_file || client?.client_logo_url}
                      alt="Client logo"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">N/A</Typography>
                  )}
                  {isEditingDetails ? (
                    <Tooltip title={logoFile ? 'Replace logo' : 'Upload logo'}>
                      <IconButton
                        size="small"
                        onClick={handleSelectLogo}
                        sx={{
                          position: 'absolute',
                          right: -8,
                          bottom: -8,
                          width: 24,
                          height: 24,
                          backgroundColor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&:hover': { backgroundColor: 'grey.100' },
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
                <Typography variant="h4" fontWeight={700}>
                  {client?.name || 'Client'}
                </Typography>
                <Chip
                  size="small"
                  label={client?.active ? 'Active' : 'Inactive'}
                  color={client?.active ? 'success' : 'default'}
                  variant={client?.active ? 'filled' : 'outlined'}
                />
                {client?.status ? <Chip size="small" label={client.status} variant="outlined" /> : null}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Client Hub | Parent: {client?.parent_company_name || 'None'}
              </Typography>
              {client?.last_backend_update ? (
                <Typography variant="caption" color="text.secondary">
                  Last backend update: {displayDate(client.last_backend_update)}
                </Typography>
              ) : null}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                component={Link}
                to={`/clients/${client?.slug || client?.id}/dashboard`}
                startIcon={<DashboardOutlinedIcon />}
                variant="outlined"
              >
                Dashboard
              </Button>
              {!isEditingDetails ? (
                <Button startIcon={<EditOutlinedIcon />} variant="contained" onClick={handleStartEdit}>
                  Edit Details
                </Button>
              ) : (
                <>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleLogoChange}
                  />
                  <Button variant="outlined" onClick={handleCancelEdit}>
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleSaveDetails} disabled={savingDetails}>
                    {savingDetails ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {detailsMessage ? (
        <Alert severity={detailsMessage.includes('saved') ? 'success' : 'error'} sx={{ mb: 2 }}>
          {detailsMessage}
        </Alert>
      ) : null}

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            {detailCards.map((section) => (
              <Card key={section.title} variant="outlined">
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Typography variant="h6" fontWeight={700}>
                      {section.title}
                    </Typography>
                    {section.title === 'Lead Routing & Webhooks' ? (
                      <Button size="small" variant="outlined" onClick={openWebhookTestDialog}>
                        Test Webhook
                      </Button>
                    ) : null}
                  </Stack>
                  <Grid container spacing={1.5}>
                    {section.fields.map((field) => (
                      <HubField
                        key={`${section.title}-${field.key}`}
                        field={field}
                        client={client}
                        draft={draft}
                        isEditing={isEditingDetails}
                        onChange={handleDraftChange}
                        jsonDraft={jsonDraft}
                        jsonErrors={jsonErrors}
                        onJsonChange={handleJsonDraftChange}
                        parentCompanyOptions={parentCompanyOptions}
                        parentCompanyLookup={parentCompanyLookup}
                      />
                    ))}
                  </Grid>
                  {section.title === 'Lead Routing & Webhooks' ? (
                    <Stack spacing={1.5} mt={2}>
                      {WEBHOOK_CONFIG_GROUPS.map((group) => {
                        const isEnabled = Boolean(enabledWebhookGroups[group.id]);
                        if (!isEnabled) {
                          return isEditingDetails ? (
                            <Box key={group.id}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleEnableWebhookGroup(group.id)}
                              >
                                Add {group.title}
                              </Button>
                            </Box>
                          ) : null;
                        }
                        const isConfigured = hasWebhookGroupData(isEditingDetails ? draft : client, group);
                        return (
                          <Accordion
                            key={group.id}
                            expanded={Boolean(expandedWebhookGroups[group.id])}
                            onChange={(_event, expanded) => handleToggleWebhookGroup(group.id, expanded)}
                            variant="outlined"
                            disableGutters
                            sx={{ '&:before': { display: 'none' } }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2" fontWeight={700}>
                                  {group.title}
                                </Typography>
                                <Chip
                                  size="small"
                                  variant="outlined"
                                  color={isConfigured ? 'success' : 'default'}
                                  label={isConfigured ? 'Configured' : 'Not configured'}
                                />
                              </Stack>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Grid container spacing={1.5}>
                                {group.fields.map((field) => (
                                  <HubField
                                    key={`${group.title}-${field.key}`}
                                    field={field}
                                    client={client}
                                  draft={draft}
                                  isEditing={isEditingDetails}
                                  onChange={handleDraftChange}
                                  jsonDraft={jsonDraft}
                                  jsonErrors={jsonErrors}
                                  onJsonChange={handleJsonDraftChange}
                                  parentCompanyOptions={parentCompanyOptions}
                                  parentCompanyLookup={parentCompanyLookup}
                                />
                              ))}
                            </Grid>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                    </Stack>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" fontWeight={700} mb={2}>
                  Related Workspace
                </Typography>
                <Stack spacing={2}>
                  {RELATED_SECTIONS.map((section) => (
                    <Box key={section.title}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        {section.title}
                      </Typography>
                      <Grid container spacing={1}>
                        {section.items.map((item) => (
                          <Grid item xs={12} sm={6} key={`${section.title}-${item.label}`}>
                            <Button
                              component={Link}
                              to={item.to(client || { id: '', slug: '' })}
                              fullWidth
                              variant="outlined"
                              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                              sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                            >
                              {item.label}
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <AccessTimeIcon fontSize="small" color="action" />
                  <Typography variant="h6" fontWeight={700}>
                    Recent Activity
                  </Typography>
                </Stack>
                {activity.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No recent activity found for this client.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {activity.map((item, idx) => (
                      <Box key={item.id}>
                        <ListItem
                          disableGutters
                          secondaryAction={
                            <Button component={Link} to={item.link} size="small" sx={{ textTransform: 'none' }}>
                              Open
                            </Button>
                          }
                          sx={{ pr: 8 }}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip size="small" label={item.source} variant="outlined" />
                                <Typography variant="body2" fontWeight={600}>
                                  {item.title}
                                </Typography>
                              </Stack>
                            }
                            secondary={
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  {item.subtitle || 'Updated'}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {displayDate(item.at)}
                                </Typography>
                              </>
                            }
                          />
                        </ListItem>
                        {idx < activity.length - 1 ? <Divider /> : null}
                      </Box>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Test Webhook</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Webhook Config</InputLabel>
              <Select
                label="Webhook Config"
                value={testConfigType}
                onChange={(e) => setTestConfigType(e.target.value)}
              >
                {availableWebhookTestOptions.map((option) => (
                  <MenuItem key={`test-config-${option.value}`} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Dummy Lead JSON"
              multiline
              minRows={12}
              value={testLeadJson}
              onChange={(e) => {
                setTestLeadJson(e.target.value);
                setTestJsonError('');
              }}
              error={Boolean(testJsonError)}
              helperText={testJsonError || 'Edit dummy lead fields used by payload mapping.'}
              sx={{ '& .MuiInputBase-input': { fontFamily: "'Roboto Mono', monospace" } }}
            />

            {testError ? <Alert severity="error">{testError}</Alert> : null}
            {testCopyMessage ? (
              <Alert severity={testCopyMessage.startsWith('Template copied') ? 'success' : 'info'}>
                {testCopyMessage}
              </Alert>
            ) : null}

            {testResult ? (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.25,
                  borderRadius: 1,
                  backgroundColor: '#0f172a',
                  color: '#e5e7eb',
                  fontFamily: "'Roboto Mono', monospace",
                  fontSize: '0.78rem',
                  lineHeight: 1.5,
                  maxHeight: 320,
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(testResult, null, 2)}
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            onClick={handleCopyWebhookTemplate}
            disabled={!testResult}
          >
            Copy Email Template
          </Button>
          <Button variant="contained" onClick={handleSendWebhookTest} disabled={testSending}>
            {testSending ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
